<template>
  <div class="post-card mb-4">
    <!-- Post Header -->
    <div class="post-header">
      <div class="d-flex align-items-center">
        <div class="user-avatar">
          <img v-if="post.author?.avatar" :src="post.author.avatar" :alt="post.authorName">
          <i v-else class="bi bi-person-circle"></i>
        </div>
        <div class="user-info">
          <div class="user-name">
            {{ post.authorName || 'Unknown User' }}
            <span v-if="post.author?.role === 'doctor'" class="badge bg-primary ms-1">Doctor</span>
            <span v-if="post.author?.role === 'admin'" class="badge bg-warning ms-1">Admin</span>
          </div>
          <small class="post-time text-muted">{{ formatTime(post.createdAt) }}</small>
        </div>
      </div>
      <div class="post-topic">
        <span class="topic-tag">{{ getTopicName(post.topic) }}</span>
      </div>
    </div>

    <!-- Post Title - Clickable -->
    <div class="post-content" @click="viewPostDetail" style="cursor: pointer;">
      <h5 v-if="post.title" class="post-title">{{ post.title }}</h5>
    </div>

    <!-- Interaction Stats Row -->
    <div class="post-stats">
      <div class="stat-item">
        <i class="bi bi-eye text-muted"></i>
        <span>{{ post.views || 0 }}</span>
      </div>
      <div class="stat-item">
        <i class="bi bi-heart-fill text-danger"></i>
        <span>{{ (post.likes || []).length }}</span>
      </div>
      <div class="stat-item">
        <i class="bi bi-chat-dots text-primary"></i>
        <span>{{ commentCount }}</span>
      </div>
      <div v-if="(post.totalRatings || 0) > 0" class="stat-item">
        <i class="bi bi-star-fill text-warning"></i>
        <span>{{ (post.averageRating || 0).toFixed(1) }} ({{ post.totalRatings }})</span>
      </div>
    </div>

    <!-- Read More Button -->
    <router-link :to="`/community/post/${post.id}`" class="read-more-btn">
      Read more <i class="bi bi-arrow-right arrow-icon"></i>
    </router-link>
  </div>
</template>

<script>
export default {
  name: 'PostCard',
  props: {
    post: {
      type: Object,
      required: true
    },
    user: {
      type: Object,
      default: null
    },
    isAuthenticated: {
      type: Boolean,
      required: true
    },
    getTopicName: {
      type: Function,
      required: true
    },
    formatTime: {
      type: Function,
      required: true
    }
  },
  emits: [
    'like-post',
    'show-rating'
  ],
  computed: {
    commentCount() {
      if (this.post.comments && Array.isArray(this.post.comments)) {
        return this.post.comments.length
      }
      return 0
    }
  },
  methods: {
    viewPostDetail() {
      this.$router.push(`/community/post/${this.post.id}`)
    }
  }
}
</script>

<style scoped>
/* Post Cards */
.post-card {
  background: white;
  border-radius: 15px;
  padding: 1.5rem;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  transition: transform 0.3s ease;
}

.post-card:hover {
  transform: translateY(-2px);
}

.post-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.user-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 1rem;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-avatar i {
  font-size: 2rem;
  color: #6c757d;
}

.user-name {
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.topic-tag {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
}

.post-content {
  margin-bottom: 1.25rem;
}

.post-title {
  color: #333;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0;
  line-height: 1.4;
  transition: color 0.3s ease;
}

.post-content:hover .post-title {
  color: #9C27B0;
}

/* Post Stats Row */
.post-stats {
  display: flex;
  gap: 1.5rem;
  padding: 1rem 0;
  margin: 1rem 0;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #6c757d;
}

.stat-item i {
  font-size: 1.1rem;
}

/* Read More Button - V3.0 Gradient Style */
.read-more-btn {
  background: linear-gradient(90deg, #9C27B0 0%, #E91E63 100%);
  color: #fff;
  padding: 8px 18px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
}

.read-more-btn:hover {
  transform: scale(1.05);
  box-shadow: 0px 4px 12px rgba(233, 30, 99, 0.4);
  color: #fff;
}

.read-more-btn .arrow-icon {
  font-size: 14px;
  transition: transform 0.2s ease;
}

.read-more-btn:hover .arrow-icon {
  transform: translateX(3px);
}

/* Responsive */
@media (max-width: 768px) {
  .post-header {
    flex-direction: column;
    gap: 1rem;
  }

  .post-title {
    font-size: 1.1rem;
  }

  .post-stats {
    gap: 1rem;
    flex-wrap: wrap;
  }

  .stat-item {
    font-size: 0.85rem;
  }

  .read-more-btn {
    font-size: 13px;
    padding: 7px 16px;
  }
}
</style>